import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import './AddEventPage.css';

const INITIAL = {
  payment_mode:        'upi_manual',
  currency:            'INR',
  // Per-tier prices in ₹ (paid at signup)
  tier_basic_price:    0,
  tier_standard_price: 200,
  tier_premium_price:  400,
  // Mobile-app access only — no physical logo/shipping. Priced separately
  // from Premium (which bundles the logo). Grants 1-yr app access on approval.
  tier_apponly_price:  200,
  // Charged on yearly renewal (independent of tier_premium_price so admins
  // can offer a loyalty discount or hike). Falls back to tier_premium_price
  // when unset, so existing deployments keep working unchanged.
  renewal_price:       400,
  // Backwards-compat (read by older code paths)
  amount_inr:          200,
  upi_id:              '',
  upi_payee_name:      'Team Fronx',
  upi_qr_url:          '',
  razorpay_key_id:     '',
  whatsapp_group_url:  '',
  // Google Form sync: public form response URL + a map of member field → the
  // form question's "entry.NNN" id. No service-account credentials needed.
  form_response_url:   '',
  form_entries: {
    timestamp: '', member_id: '', name: '', phone: '', email: '',
    district: '', car_variant: '', engine_variant: '', car_color: '',
    vehicle_number: '', blood_group: '', emergency_contact: '',
    expiry: '', payment_id: '', amount: '',
  },
  signup_enabled:      true,
  membership_years:    1,
  success_message:     "Welcome to Team Fronx! We'll verify your payment shortly and contact you with the WhatsApp link.",
};

// Member fields that can be mapped to Google Form questions. Keys MUST match
// the `values` object in the verifyMembershipPayment Cloud Function.
const FORM_FIELDS = [
  ['timestamp',         'Timestamp'],
  ['member_id',         'Member ID'],
  ['name',              'Name'],
  ['phone',             'Phone'],
  ['email',             'Email'],
  ['district',          'District'],
  ['car_variant',       'Car Variant'],
  ['engine_variant',    'Engine Variant'],
  ['car_color',         'Car Color'],
  ['vehicle_number',    'Vehicle Number'],
  ['blood_group',       'Blood Group'],
  ['emergency_contact', 'Emergency Contact'],
  ['expiry',            'Membership Expiry'],
  ['payment_id',        'Payment ID'],
  ['amount',            'Amount'],
];

export default function MembershipConfigPage() {
  const [form,       setForm]       = useState(INITIAL);
  const [loaded,     setLoaded]     = useState(false);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [qrFile,     setQrFile]     = useState(null);
  const [qrPreview,  setQrPreview]  = useState('');
  const qrInputRef = useRef(null);

  const handleQrChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: '❌ Please pick an image.' });
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setToast({ type: 'error', message: '❌ Max 3 MB.' });
      return;
    }
    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
  }, []);

  const clearQr = useCallback(() => {
    setQrFile(null);
    setQrPreview('');
    setForm(p => ({ ...p, upi_qr_url: '' }));
    if (qrInputRef.current) qrInputRef.current.value = '';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_config', 'membership'));
        if (snap.exists()) setForm({ ...INITIAL, ...snap.data() });
      } catch { /* defaults */ }
      setLoaded(true);
    })();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({
      ...p,
      [name]: type === 'checkbox' ? checked
            : type === 'number'   ? Number(value)
            : value,
    }));
    setErrors(p => ({ ...p, [name]: undefined }));
  }, []);

  // One Google Form "entry.NNN" id per member field.
  const handleEntryChange = useCallback((key, value) => {
    setForm(p => ({
      ...p,
      form_entries: { ...p.form_entries, [key]: value },
    }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.amount_inr || form.amount_inr < 1)
      e.amount_inr = 'Amount must be > 0';
    if (form.payment_mode === 'upi_manual') {
      if (!form.upi_id.trim()) e.upi_id = 'UPI ID is required for manual payment.';
      else if (!/^[\w.\-]+@[\w.\-]+$/.test(form.upi_id.trim()))
        e.upi_id = 'Use the format name@bank, e.g. teamfronx@oksbi';
    }
    if (form.razorpay_key_id.trim() &&
        !/^rzp_(test|live)_[A-Za-z0-9]+$/.test(form.razorpay_key_id.trim()))
      e.razorpay_key_id = 'Must start with rzp_test_ or rzp_live_';
    if (form.whatsapp_group_url.trim() &&
        !/^https:\/\/chat\.whatsapp\.com\//.test(form.whatsapp_group_url.trim()))
      e.whatsapp_group_url = 'Must be a https://chat.whatsapp.com/... link';
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      // Upload QR code if a new one was picked.
      let upi_qr_url = form.upi_qr_url;
      if (qrFile) {
        const ext = (qrFile.name.split('.').pop() || 'png').toLowerCase();
        const path = `payment_config/upi_qr.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, qrFile, { contentType: qrFile.type });
        upi_qr_url = await getDownloadURL(storageRef);
      }

      await setDoc(doc(db, 'app_config', 'membership'), {
        ...form,
        upi_id:             form.upi_id.trim(),
        upi_payee_name:     form.upi_payee_name.trim() || 'Team Fronx',
        upi_qr_url,
        razorpay_key_id:    form.razorpay_key_id.trim(),
        whatsapp_group_url: form.whatsapp_group_url.trim(),
        form_response_url:  form.form_response_url.trim(),
        form_entries:       Object.fromEntries(
          Object.entries(form.form_entries || {})
            .map(([k, v]) => [k, String(v).trim()]),
        ),
        success_message:    form.success_message.trim(),
        updated_at:         serverTimestamp(),
      });
      setForm(p => ({ ...p, upi_qr_url }));
      setQrFile(null);
      setToast({ type: 'success', message: '✅ Membership settings saved.' });
    } catch (err) {
      setToast({ type: 'error', message: `❌ Failed: ${err.message}` });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  if (!loaded) {
    return <div className="mem-loading"><span className="tf-spinner tf-spinner--red" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Membership Settings</h1>
          <p className="page-subtitle">
            Configure the public signup link, payment, WhatsApp invite,
            and Google Form sync.
          </p>
        </div>
      </div>

      {toast && (
        <div className={`amf-toast amf-toast--${toast.type}`} style={{ marginBottom: 24 }}>
          {toast.message}
          <button className="amf-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div className="aep-grid">

          {/* ── Left column: pricing & signup toggle ── */}
          <div className="aep-col">
            <SectionCard title="🏷️ Tier Pricing">
              <p style={{ color: '#B79F6D', fontSize: 12, lineHeight: 1.55, marginBottom: 14 }}>
                Four tiers shown on the public signup page.
                Basic = WhatsApp only. Standard adds a physical logo (with
                shipping address). Team Fronx Mobile Access = 1-year mobile-app
                access with no logo. Premium bundles both the logo and 1-year app access.
              </p>

              <Field label="Basic — ₹ (WhatsApp only)">
                <input name="tier_basic_price" type="number" min={0}
                  className="amf-input"
                  value={form.tier_basic_price} onChange={handleChange} />
                <p className="amf-hint">Usually free (₹ 0).</p>
              </Field>

              <Field label="Standard — ₹ (WhatsApp + logo shipped)">
                <input name="tier_standard_price" type="number" min={0}
                  className="amf-input"
                  value={form.tier_standard_price} onChange={handleChange} />
              </Field>

              <Field label="Team Fronx Mobile Access — ₹ (WhatsApp + 1-yr app, no logo)">
                <input name="tier_apponly_price" type="number" min={0}
                  className="amf-input"
                  value={form.tier_apponly_price} onChange={handleChange} />
                <p className="amf-hint">
                  Mobile-app access without the physical logo — a separate,
                  usually cheaper option than Premium.
                </p>
              </Field>

              <Field label="Premium — ₹ (WhatsApp + logo + 1-yr app)">
                <input name="tier_premium_price" type="number" min={0}
                  className="amf-input"
                  value={form.tier_premium_price} onChange={handleChange} />
              </Field>

              <Field label="Renewal — ₹ (yearly, public /renew page)">
                <input name="renewal_price" type="number" min={0}
                  className="amf-input"
                  value={form.renewal_price} onChange={handleChange} />
                <p className="amf-hint">
                  Charged when an existing member renews their expired
                  Premium membership. Independent from the signup price
                  above, so you can offer renewals at a different rate.
                </p>
              </Field>

              <Field label="Premium Membership Duration (years)">
                <input name="membership_years" type="number" min={1} max={10}
                  className="amf-input"
                  value={form.membership_years} onChange={handleChange} />
                <p className="amf-hint">
                  Only applies to Premium — they get this many years of
                  mobile-app access starting from approval.
                </p>
              </Field>
            </SectionCard>

            <SectionCard title="💸 Payment Mode">
              <Field label="How members pay">
                <select name="payment_mode" className="amf-select"
                  value={form.payment_mode} onChange={handleChange}>
                  <option value="upi_manual">UPI + manual admin verify (Recommended)</option>
                  <option value="razorpay">Razorpay automatic</option>
                </select>
                <p className="amf-hint">
                  <strong>UPI manual</strong>: user pays via your UPI ID/QR,
                  uploads a screenshot, you review &amp; approve from the
                  Pending Applications page.<br /><strong>Razorpay</strong>:
                  fully automated, no manual step. Requires Razorpay keys.
                </p>
              </Field>
            </SectionCard>

            <SectionCard title="📱 UPI Details">
              <Field label="UPI ID" required error={errors.upi_id}>
                <input name="upi_id"
                  className={`amf-input ${errors.upi_id ? 'amf-input--error' : ''}`}
                  placeholder="teamfronx@oksbi"
                  value={form.upi_id} onChange={handleChange} />
                <p className="amf-hint">
                  Members will see this and a "Pay" deep-link to their
                  preferred UPI app.
                </p>
              </Field>

              <Field label="Payee Name (shown in UPI apps)">
                <input name="upi_payee_name" className="amf-input"
                  placeholder="Team Fronx"
                  value={form.upi_payee_name} onChange={handleChange} />
              </Field>

              <Field label="UPI QR Code (optional)">
                {(qrPreview || form.upi_qr_url) ? (
                  <div style={{
                    position: 'relative',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid rgba(212, 181, 116, 0.25)',
                  }}>
                    <img src={qrPreview || form.upi_qr_url} alt="QR"
                      style={{
                        width: '100%', maxHeight: 280,
                        objectFit: 'contain',
                        background: '#fff', display: 'block',
                      }} />
                    <button type="button" onClick={clearQr}
                      style={{
                        position: 'absolute', top: 8, right: 8,
                        padding: '6px 12px',
                        background: 'rgba(0,0,0,0.75)',
                        border: '1px solid #E63946',
                        borderRadius: 6,
                        color: '#E63946', fontSize: 12, fontWeight: 600,
                        cursor: 'pointer',
                      }}>Remove</button>
                  </div>
                ) : (
                  <button type="button"
                    onClick={() => qrInputRef.current?.click()}
                    style={{
                      width: '100%',
                      padding: '24px 16px',
                      background: '#161618',
                      border: '2px dashed rgba(212, 181, 116, 0.35)',
                      borderRadius: 8,
                      color: '#D8C8A4', fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 6,
                    }}>
                    <span style={{ fontSize: 26 }}>📱</span>
                    <span style={{ fontWeight: 700 }}>Click to upload QR</span>
                    <span style={{ color: '#B79F6D', fontSize: 11 }}>
                      JPG or PNG · max 3 MB
                    </span>
                  </button>
                )}
                <input ref={qrInputRef} type="file" accept="image/*"
                  style={{ display: 'none' }} onChange={handleQrChange} />
                <p className="amf-hint">
                  Generate from any UPI app or service like
                  upiqr.in. Shown to members on the signup page.
                </p>
              </Field>
            </SectionCard>

            <SectionCard title="💳 Razorpay (for future automated payments)">
              <Field label="Razorpay Key ID (optional)" error={errors.razorpay_key_id}>
                <input name="razorpay_key_id"
                  className={`amf-input ${errors.razorpay_key_id ? 'amf-input--error' : ''}`}
                  placeholder="rzp_test_xxx or rzp_live_xxx"
                  value={form.razorpay_key_id} onChange={handleChange} />
              </Field>
            </SectionCard>

            <SectionCard title="🟢 Signup Status">
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer',
              }}>
                <input name="signup_enabled" type="checkbox"
                  checked={form.signup_enabled}
                  onChange={handleChange}
                  style={{ width: 18, height: 18 }} />
                <span style={{ color: '#D8C8A4', fontSize: 14 }}>
                  Public signup is <strong>{form.signup_enabled ? 'OPEN' : 'CLOSED'}</strong>
                </span>
              </label>
              <p className="amf-hint" style={{ marginTop: 8 }}>
                Uncheck to temporarily disable the public /signup page
                (e.g., when full).
              </p>
            </SectionCard>
          </div>

          {/* ── Right column: WhatsApp + Sheets + success message ── */}
          <div className="aep-col">
            <SectionCard title="💬 WhatsApp Group">
              <Field label="Group Invite URL" error={errors.whatsapp_group_url}>
                <input name="whatsapp_group_url" type="url"
                  className={`amf-input ${errors.whatsapp_group_url ? 'amf-input--error' : ''}`}
                  placeholder="https://chat.whatsapp.com/XXXXXXXXXXX"
                  value={form.whatsapp_group_url} onChange={handleChange} />
                <p className="amf-hint">
                  Get this from WhatsApp → group → Invite via link.
                  Shown on the success screen after payment.
                </p>
              </Field>
            </SectionCard>

            <SectionCard title="📋 Google Form Sync">
              <p className="amf-hint" style={{ marginBottom: 12 }}>
                Each new paid membership is submitted to your Google Form — no
                service account or credentials needed. Responses land in the
                form (and its linked sheet, if you connect one). Paste the
                form's <strong>response URL</strong> and each question's
                <strong> entry ID</strong> below. Leave any field blank to skip it.
              </p>
              <Field label="Form response URL">
                <input name="form_response_url"
                  className="amf-input"
                  placeholder="https://docs.google.com/forms/d/e/FORM_ID/formResponse"
                  value={form.form_response_url} onChange={handleChange} />
                <p className="amf-hint">
                  Open your form → ⋮ menu → <strong>Get pre-filled link</strong>,
                  fill each field with any placeholder, click Get link, and copy
                  the URL. Change the trailing <code>viewform?…</code> to
                  <code> formResponse</code> for this box; the
                  <code> entry.NNN</code> numbers in that link are the IDs below.
                </p>
              </Field>
              {FORM_FIELDS.map(([key, label]) => (
                <Field key={key} label={`${label} → entry ID`}>
                  <input
                    className="amf-input"
                    placeholder="entry.123456789"
                    value={form.form_entries?.[key] || ''}
                    onChange={(e) => handleEntryChange(key, e.target.value)} />
                </Field>
              ))}
            </SectionCard>

            <SectionCard title="🎉 Success Screen Text">
              <Field label="Message shown after successful payment">
                <textarea name="success_message"
                  className="amf-input aep-textarea"
                  rows={3}
                  value={form.success_message} onChange={handleChange} />
              </Field>
            </SectionCard>
          </div>
        </div>

        <div className="aep-submit-row">
          <button type="submit" className="amf-submit-btn" disabled={submitting}>
            {submitting
              ? <><span className="amf-spinner" /> Saving…</>
              : 'Save Membership Settings'}
          </button>
        </div>
      </form>

      <div style={{
        marginTop: 32,
        padding: 16,
        background: 'rgba(34, 197, 94, 0.06)',
        border: '1px solid rgba(34, 197, 94, 0.22)',
        borderRadius: 10,
      }}>
        <div style={{
          color: '#22C55E',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}>
          Public Signup Link
        </div>
        <div style={{ color: '#F7E8BF', fontSize: 13, marginBottom: 6 }}>
          Share this URL anywhere — anyone can sign up:
        </div>
        <div style={{
          padding: '10px 14px',
          background: '#0F0F0F',
          border: '1px solid rgba(212, 181, 116, 0.25)',
          borderRadius: 6,
          color: '#D4B574',
          fontFamily: 'monospace',
          fontSize: 12,
          userSelect: 'all',
          cursor: 'text',
        }}>
          https://team-fronx-32bbe.web.app/signup
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{
      marginBottom: 20,
      padding: 16,
      background: 'rgba(212, 181, 116, 0.04)',
      border: '1px solid rgba(212, 181, 116, 0.18)',
      borderRadius: 10,
    }}>
      <div style={{
        color: '#D4B574',
        fontSize: 11,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        fontWeight: 700,
        marginBottom: 12,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}
